import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Chip, Menu, MenuItem, Tooltip, ListItemIcon, ListItemText } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import CheckIcon from '@material-ui/icons/Check'
import { fetchTenants } from '../../redux/actions/dashboard'
import { config } from '../../config'
import { Tenant } from '../../shared/types/tenant'
import {
    setTenantFilter,
    TENANT_FILTER_UNSUPPORTED_PATHS,
    useIsSuperAdmin,
    useTenantFilter,
} from '../../utils/tenantFilter'

const useStyles = makeStyles((theme) => ({
    wrapper: {
        marginLeft: 'auto',
        marginRight: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
    },
    allButton: {
        textTransform: 'none',
        color: theme.palette.text.secondary,
    },
    activeChip: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 600,
        '& .MuiChip-deleteIcon': {
            color: theme.palette.primary.contrastText,
            opacity: 0.8,
        },
        '&:hover, &:focus': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    checkIcon: {
        minWidth: 28,
    },
}))

const TenantSwitcher = () => {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const location = useLocation()
    const isSuperAdmin = useIsSuperAdmin()
    const tenantFilter = useTenantFilter()
    const tenants: Tenant[] = useSelector((state: any) => state.tenants.value) ?? []
    const tenantsExecuted = useSelector((state: any) => state.tenants.executed)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    useEffect(() => {
        if (isSuperAdmin && !tenantsExecuted && tenants.length === 0) {
            dispatch(fetchTenants())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuperAdmin])

    if (!isSuperAdmin) {
        return null
    }

    const unsupported = TENANT_FILTER_UNSUPPORTED_PATHS.includes(location.pathname)
    const selectedTenant = tenants.find((x) => x.id === tenantFilter)
    const selectedName = selectedTenant?.name ?? (tenantFilter ? '…' : '')

    const select = (tenantId: string | null) => {
        dispatch(setTenantFilter(tenantId))
        setAnchorEl(null)
    }

    const control = tenantFilter ? (
        <Chip
            className={classes.activeChip}
            label={`${t('app.tenant')}: ${selectedName}`}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            onDelete={() => select(null)}
            disabled={unsupported}
        />
    ) : (
        <Button
            className={classes.allButton}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            endIcon={<ExpandMoreIcon />}
            disabled={unsupported}
        >
            {t('app.tenant')}: {t('app.allTenants')}
        </Button>
    )

    return (
        <div className={classes.wrapper}>
            <Tooltip title={unsupported ? (t('app.tenantFilterNotApplied') as string) : ''}>
                <span>{control}</span>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => select(null)}>
                    <ListItemIcon className={classes.checkIcon}>
                        {!tenantFilter ? <CheckIcon fontSize="small" /> : <span />}
                    </ListItemIcon>
                    <ListItemText primary={t('app.allTenants')} />
                </MenuItem>
                {[...tenants]
                    // hlavny tenant (superadmin) = "All tenants", v zozname ho neuvadzame
                    .filter((tenant) => tenant.id !== config.mainTenantId)
                    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                    .map((tenant) => (
                        <MenuItem key={tenant.id} onClick={() => select(tenant.id)}>
                            <ListItemIcon className={classes.checkIcon}>
                                {tenantFilter === tenant.id ? <CheckIcon fontSize="small" /> : <span />}
                            </ListItemIcon>
                            <ListItemText primary={tenant.name} />
                        </MenuItem>
                    ))}
            </Menu>
        </div>
    )
}

export default TenantSwitcher
